import {isFeatureEnabled} from 'feature-flags';
import {Dropdown, IconButton, Tooltip} from 'platform/components';
import {Box, Center, Heading, Hide, Scroll, Show, VStack} from 'platform/foundation';

import {UserSidebarIconSize} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {useFocusControl, useTenant} from '@dms/shared';

import {pushGtmEvent, suffixTestId, TestIdProps, useBoolean} from 'shared/web';

import {omneticReleaseNotes} from '../../../releaseNotes';
import {HeaderButtonWrapper} from './HeaderButtonWrapper';
import {Release} from './Release';
import {TopBarActionContainer} from './TopBarActionContainer';

interface ReleasePopupProps extends TestIdProps {
  iconSize: UserSidebarIconSize;
}

export function ReleasePopup(props: ReleasePopupProps) {
  const [isOpen, setOpen, setClose] = useBoolean();
  const {isDisabled} = useFocusControl();
  const {tenantCountry} = useTenant();

  const onOpen = () => {
    pushGtmEvent({
      event: 'button_click',
      event_id: 'top_bar_release_popup_open',
    });
    setOpen();
  };

  const isBeamerEnabledInWindow = window?.Beamer?.enabled === true;

  return (
    <>
      <Show when={isFeatureEnabled(featureFlags.SCRIPT_BEAMER) && isBeamerEnabledInWindow}>
        <TopBarActionContainer>
          <Center width="100%" height="100%">
            {/* eslint-disable-next-line react/forbid-dom-props */}
            <div className="beamerTrigger">
              <Tooltip label={i18n.t('general.labels.releaseNotesTitle')} placement="bottom">
                <IconButton
                  aria-label="topBar"
                  iconSize={props.iconSize}
                  icon="action/grade"
                  tabIndex={isDisabled ? -1 : 0}
                  data-testid={suffixTestId('releasePopup-button', props)}
                />
              </Tooltip>
            </div>
          </Center>
        </TopBarActionContainer>
      </Show>
      <Hide when={isFeatureEnabled(featureFlags.SCRIPT_BEAMER) && isBeamerEnabledInWindow}>
        <Dropdown
          onOpen={onOpen}
          onClose={setClose}
          closeOnBlur
          data-testid={suffixTestId('releasePopupDropdown', props)}
          dropdownControl={
            <TopBarActionContainer>
              <Center width="100%" height="100%">
                <HeaderButtonWrapper $isOpen={isOpen}>
                  <Tooltip label={i18n.t('general.labels.releaseNotesTitle')} placement="bottom">
                    <IconButton
                      aria-label="topBar"
                      iconSize={props.iconSize}
                      icon="action/grade"
                      tabIndex={isDisabled ? -1 : 0}
                      data-testid={suffixTestId('releasePopup-button', props)}
                    />
                  </Tooltip>
                </HeaderButtonWrapper>
              </Center>
            </TopBarActionContainer>
          }
          placement="bottom-end"
          isLazy
          lazyBehavior="unmount"
        >
          <Box data-testid={suffixTestId('releasePopup', props)}>
            <Scroll width={120} maxWidth={200} maxHeight="85vh" auto>
              <VStack data-testid={suffixTestId('releasePopupInnerBox', props)}>
                <Box padding={2}>
                  <Center justify="flex-start" minHeight={8}>
                    <Heading size={4}>{i18n.t('general.labels.releaseNotesTitle')}</Heading>
                  </Center>
                </Box>
                <VStack spacing={1}>
                  {omneticReleaseNotes.map((release, index) => (
                    <Hide
                      whenFeatureDisabled={release.featureName}
                      key={`release-[${release.url}]`}
                    >
                      <Release
                        data-testid={`release-[${index}]`}
                        type={release.type}
                        title={release.title}
                        description={release.description}
                        dateTime={release.dateTime}
                        url={release.urlByCountry?.[tenantCountry] || release.url}
                        versionNumber={release.versionNumber}
                      />
                    </Hide>
                  ))}
                </VStack>
              </VStack>
            </Scroll>
          </Box>
        </Dropdown>
      </Hide>
    </>
  );
}
